import { VALUE_FORMAT_EPOCH_INPUT_PATTERN, VALUE_FORMAT_LOCALE } from "@/constants";

/**
 * Reading and writing dates with token patterns such as `DD/MM/YYYY` or `MMM D, YYYY h:mm A`.
 *
 * Works on calendar fields rather than on instants: a value is shown as the wall-clock date and
 * time the data says, never shifted into the viewer's time zone. The warehouse has already
 * applied its own zone, so `2024-01-15` must read as the 15th for a viewer in New York and one in
 * Tokyo alike; turning it into a `Date` and back would show the 14th in one of them.
 */

/** A calendar date and wall-clock time, in no time zone. `month` is 1-12. */
export type DateParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
  millisecond: number;
};

type DatePatternPart = { token: string } | { literal: string };

/**
 * Longest tokens first, so `YYYY` is never read as two `YY`s. `[text]` is a literal, which is how
 * a pattern prints a letter that would otherwise be a token.
 */
const DATE_PATTERN_TOKEN =
  /\[([^\]]*)]|YYYY|YY|MMMM|MMM|MM|M|dddd|ddd|DD|D|HH|H|hh|h|mm|m|ss|s|SSS|A|a/g;

/** ISO 8601 as the warehouse sends it: a date, optionally a time, optionally an offset. */
const ISO_DATE_TIME =
  /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2})(?:[.,](\d+))?)?)?\s*(?:Z|[+-]\d{2}(?::?\d{2})?)?$/i;

const parsedPatterns = new Map<string, DatePatternPart[]>();

function patternParts(pattern: string): DatePatternPart[] {
  const cached = parsedPatterns.get(pattern);
  if (cached !== undefined) {
    return cached;
  }
  const parts: DatePatternPart[] = [];
  let consumed = 0;
  for (const match of pattern.matchAll(DATE_PATTERN_TOKEN)) {
    if (match.index > consumed) {
      parts.push({ literal: pattern.slice(consumed, match.index) });
    }
    parts.push(match[1] !== undefined ? { literal: match[1] } : { token: match[0] });
    consumed = match.index + match[0].length;
  }
  if (consumed < pattern.length) {
    parts.push({ literal: pattern.slice(consumed) });
  }
  parsedPatterns.set(pattern, parts);
  return parts;
}

/** Whether `pattern` holds at least one date or time token, so it would show something of a date. */
export function hasDateToken(pattern: string): boolean {
  return patternParts(pattern).some((part) => "token" in part);
}

/** Whether `pattern` reads a number as Unix time rather than as calendar text. */
export function isEpochInputPattern(pattern: string): boolean {
  return (
    pattern === VALUE_FORMAT_EPOCH_INPUT_PATTERN.SECONDS ||
    pattern === VALUE_FORMAT_EPOCH_INPUT_PATTERN.MILLISECONDS
  );
}

type CalendarNames = { long: string[]; short: string[] };

let monthNamesCache: CalendarNames | null = null;
let weekdayNamesCache: CalendarNames | null = null;

function namesFor(
  count: number,
  dateAt: (index: number) => number,
  field: "month" | "weekday",
): CalendarNames {
  const read = (width: "long" | "short") => {
    const formatter = new Intl.DateTimeFormat(VALUE_FORMAT_LOCALE, {
      [field]: width,
      timeZone: "UTC",
    });
    return Array.from({ length: count }, (_unused, index) => formatter.format(dateAt(index)));
  };
  return { long: read("long"), short: read("short") };
}

function monthNames(): CalendarNames {
  monthNamesCache ??= namesFor(12, (index) => Date.UTC(2000, index, 1), "month");
  return monthNamesCache;
}

/** Sunday first, matching `Date.getUTCDay`. 1 January 2023 was a Sunday. */
function weekdayNames(): CalendarNames {
  weekdayNamesCache ??= namesFor(7, (index) => Date.UTC(2023, 0, 1 + index), "weekday");
  return weekdayNamesCache;
}

function padNumber(value: number, width: number): string {
  return String(Math.abs(value)).padStart(width, "0");
}

/** A `Date` in UTC holding `parts`; years 0-99 are kept as written rather than moved to 19xx. */
function utcDateOf(parts: DateParts): Date {
  const date = new Date(0);
  date.setUTCFullYear(parts.year, parts.month - 1, parts.day);
  date.setUTCHours(parts.hour, parts.minute, parts.second, parts.millisecond);
  return date;
}

function twelveHourOf(hour: number): number {
  return hour % 12 === 0 ? 12 : hour % 12;
}

function formatToken(token: string, parts: DateParts): string {
  switch (token) {
    case "YYYY":
      return padNumber(parts.year, 4);
    case "YY":
      return padNumber(parts.year % 100, 2);
    case "MMMM":
      return monthNames().long[parts.month - 1] ?? "";
    case "MMM":
      return monthNames().short[parts.month - 1] ?? "";
    case "MM":
      return padNumber(parts.month, 2);
    case "M":
      return String(parts.month);
    case "dddd":
      return weekdayNames().long[utcDateOf(parts).getUTCDay()] ?? "";
    case "ddd":
      return weekdayNames().short[utcDateOf(parts).getUTCDay()] ?? "";
    case "DD":
      return padNumber(parts.day, 2);
    case "D":
      return String(parts.day);
    case "HH":
      return padNumber(parts.hour, 2);
    case "H":
      return String(parts.hour);
    case "hh":
      return padNumber(twelveHourOf(parts.hour), 2);
    case "h":
      return String(twelveHourOf(parts.hour));
    case "mm":
      return padNumber(parts.minute, 2);
    case "m":
      return String(parts.minute);
    case "ss":
      return padNumber(parts.second, 2);
    case "s":
      return String(parts.second);
    case "SSS":
      return padNumber(parts.millisecond, 3);
    case "A":
      return parts.hour < 12 ? "AM" : "PM";
    case "a":
      return parts.hour < 12 ? "am" : "pm";
    default:
      return token;
  }
}

/** `parts` written out in `pattern`. Text that is not a token is copied through unchanged. */
export function formatDateParts(parts: DateParts, pattern: string): string {
  return patternParts(pattern)
    .map((part) => ("token" in part ? formatToken(part.token, parts) : part.literal))
    .join("");
}

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** One capture group per token; names are matched case-insensitively, longest first. */
function tokenSource(token: string): string {
  const namesSource = (names: string[]) =>
    `(${[...names]
      .sort((first, second) => second.length - first.length)
      .map(escapeRegExp)
      .join("|")})`;
  switch (token) {
    case "YYYY":
      return "(\\d{4})";
    case "YY":
    case "MM":
    case "DD":
    case "HH":
    case "hh":
    case "mm":
    case "ss":
      return "(\\d{2})";
    case "M":
    case "D":
    case "H":
    case "h":
    case "m":
    case "s":
      return "(\\d{1,2})";
    case "SSS":
      return "(\\d{1,3})";
    case "MMMM":
      return namesSource(monthNames().long);
    case "MMM":
      return namesSource(monthNames().short);
    case "dddd":
      return namesSource(weekdayNames().long);
    case "ddd":
      return namesSource(weekdayNames().short);
    case "A":
    case "a":
      return "(am|pm)";
    default:
      return escapeRegExp(token);
  }
}

function monthFromName(name: string, names: string[]): number {
  const index = names.findIndex((candidate) => candidate.toLowerCase() === name.toLowerCase());
  return index + 1;
}

function daysInMonth(year: number, month: number): number {
  return utcDateOf({
    year,
    month: month + 1,
    day: 0,
    hour: 0,
    minute: 0,
    second: 0,
    millisecond: 0,
  }).getUTCDate();
}

/** Whether every field is in range, including the day for its month and year. */
function isValidDateParts(parts: DateParts): boolean {
  return (
    parts.month >= 1 &&
    parts.month <= 12 &&
    parts.day >= 1 &&
    parts.day <= daysInMonth(parts.year, parts.month) &&
    parts.hour >= 0 &&
    parts.hour <= 23 &&
    parts.minute >= 0 &&
    parts.minute <= 59 &&
    parts.second >= 0 &&
    parts.second <= 59
  );
}

/**
 * `text` read with `pattern`, or null when it does not match or names an impossible date. A field
 * the pattern leaves out takes the start of its range: `MMM YYYY` reads as the 1st of the month.
 */
function parseWithPattern(text: string, pattern: string): DateParts | null {
  const tokens: string[] = [];
  const source = patternParts(pattern)
    .map((part) => {
      if ("literal" in part) {
        return escapeRegExp(part.literal);
      }
      tokens.push(part.token);
      return tokenSource(part.token);
    })
    .join("");
  const match = new RegExp(`^${source}$`, "i").exec(text.trim());
  if (match === null) {
    return null;
  }
  const parts: DateParts = {
    year: 1970,
    month: 1,
    day: 1,
    hour: 0,
    minute: 0,
    second: 0,
    millisecond: 0,
  };
  let meridiem: string | null = null;
  tokens.forEach((token, index) => {
    const captured = match[index + 1];
    const numeric = Number(captured);
    switch (token) {
      case "YYYY":
        parts.year = numeric;
        break;
      case "YY":
        // The same pivot moment and dayjs use: 69-99 is the 1900s, 00-68 the 2000s.
        parts.year = numeric > 68 ? 1900 + numeric : 2000 + numeric;
        break;
      case "MMMM":
        parts.month = monthFromName(captured, monthNames().long);
        break;
      case "MMM":
        parts.month = monthFromName(captured, monthNames().short);
        break;
      case "MM":
      case "M":
        parts.month = numeric;
        break;
      case "DD":
      case "D":
        parts.day = numeric;
        break;
      case "HH":
      case "H":
      case "hh":
      case "h":
        parts.hour = numeric;
        break;
      case "mm":
      case "m":
        parts.minute = numeric;
        break;
      case "ss":
      case "s":
        parts.second = numeric;
        break;
      case "SSS":
        parts.millisecond = Number(captured.padEnd(3, "0"));
        break;
      case "A":
      case "a":
        meridiem = captured.toLowerCase();
        break;
      default:
        // A weekday name is checked for shape but carries nothing the date does not.
        break;
    }
  });
  if (meridiem !== null) {
    if (parts.hour < 1 || parts.hour > 12) {
      return null;
    }
    parts.hour = (parts.hour % 12) + (meridiem === "pm" ? 12 : 0);
  }
  return isValidDateParts(parts) ? parts : null;
}

function parseIsoDateTime(text: string): DateParts | null {
  const match = ISO_DATE_TIME.exec(text.trim());
  if (match === null) {
    return null;
  }
  const [, year, month, day, hour, minute, second, fraction] = match;
  const parts: DateParts = {
    year: Number(year),
    month: Number(month),
    day: Number(day),
    hour: hour === undefined ? 0 : Number(hour),
    minute: minute === undefined ? 0 : Number(minute),
    second: second === undefined ? 0 : Number(second),
    millisecond: fraction === undefined ? 0 : Number(fraction.slice(0, 3).padEnd(3, "0")),
  };
  return isValidDateParts(parts) ? parts : null;
}

function partsOfUtcDate(date: Date): DateParts {
  return {
    year: date.getUTCFullYear(),
    month: date.getUTCMonth() + 1,
    day: date.getUTCDate(),
    hour: date.getUTCHours(),
    minute: date.getUTCMinutes(),
    second: date.getUTCSeconds(),
    millisecond: date.getUTCMilliseconds(),
  };
}

function partsOfLocalDate(date: Date): DateParts {
  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
    hour: date.getHours(),
    minute: date.getMinutes(),
    second: date.getSeconds(),
    millisecond: date.getMilliseconds(),
  };
}

/** Unix time, in UTC: an instant has no wall clock of its own, and UTC reads the same for all. */
function parseEpoch(value: unknown, pattern: string): DateParts | null {
  const numeric =
    typeof value === "number"
      ? value
      : typeof value === "string" && value.trim() !== ""
        ? Number(value.trim())
        : Number.NaN;
  if (!Number.isFinite(numeric)) {
    return null;
  }
  const date = new Date(
    pattern === VALUE_FORMAT_EPOCH_INPUT_PATTERN.SECONDS ? numeric * 1000 : numeric,
  );
  return Number.isNaN(date.getTime()) ? null : partsOfUtcDate(date);
}

/**
 * A cell value read as a date, or null when it is not one.
 *
 * With no `inputPattern`, reads what the warehouse sends for date and timestamp columns -- ISO
 * text such as `2024-01-15` or `2024-01-15T10:30:00+00:00` -- and a `Date`. Any offset is ignored
 * so the date and time are shown as written. A number is not guessed at: `20240115` and an epoch
 * look alike, so reading either needs a pattern (`YYYYMMDD`, or `X`/`x` for Unix seconds/ms).
 */
export function parseDateValue(
  value: unknown,
  inputPattern?: string | null,
): DateParts | null {
  if (value === null || value === undefined) {
    return null;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : partsOfLocalDate(value);
  }
  const pattern = inputPattern?.trim() ?? "";
  if (pattern !== "") {
    if (isEpochInputPattern(pattern)) {
      return parseEpoch(value, pattern);
    }
    if (typeof value !== "string" && typeof value !== "number") {
      return null;
    }
    return parseWithPattern(String(value), pattern);
  }
  return typeof value === "string" ? parseIsoDateTime(value) : null;
}

/**
 * `parts` as milliseconds for a chart's time axis, which places points by instant. Taken in the
 * browser's zone, and read back by `datePartsFromLocalTimestamp`, so the axis shows the same wall
 * clock the data says.
 */
export function datePartsToLocalTimestamp(parts: DateParts): number {
  const date = new Date(0);
  date.setFullYear(parts.year, parts.month - 1, parts.day);
  date.setHours(parts.hour, parts.minute, parts.second, parts.millisecond);
  return date.getTime();
}

/** The wall clock behind a time-axis value made by `datePartsToLocalTimestamp`. */
export function datePartsFromLocalTimestamp(timestamp: number): DateParts | null {
  const date = new Date(timestamp);
  return Number.isNaN(date.getTime()) ? null : partsOfLocalDate(date);
}
